import { getPegboardHoleCount } from "./pegboardHoleCounts";

export type RowDirection = "LTR" | "RTL";

export type BayRowOrder = "TopFirst" | "BottomFirst";

export interface SnakingItem {
  cdt3Priority: number;
}

export interface SnakingShelf<Item extends SnakingItem = SnakingItem> {
  uniqueId: string;
  type: "REGULAR" | "RISER" | "HANGCELL";
  items: Item[];
  mergedLeft: SnakingShelf<Item> | null;
  mergedRight: SnakingShelf<Item> | null;
  /** Bottom-up physical shelf surface, when the adapter exposes it. */
  yBottom?: number;
  height?: number;
  getYBottom?: () => number;
  getName?: () => string;
  bay?: { uniqueId: string };
}

export interface PegboardHoleAddress {
  holeNoLeft: number;
  holeNoTop: number;
}

export interface SnakingPegboardPlacement<Item extends SnakingItem = SnakingItem> {
  uniqueId: string;
  holes: PegboardHoleAddress[];
  pegProduct: Item;
}

export interface SnakingPegboard<Item extends SnakingItem = SnakingItem> {
  uniqueId: string;
  yBottom: number;
  height: number;
  holesOffsetHorizontal: number;
  holesOffsetVertical: number;
  holesGapHorizontal: number;
  holesGapVertical: number;
  placements: SnakingPegboardPlacement<Item>[];
  bay?: { uniqueId: string };
}

export interface SnakingBay<
  Item extends SnakingItem = SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
> {
  uniqueId: string;
  shelves: Shelf[];
  pegboards: SnakingPegboard<Item>[];
}

export interface SnakingPog<
  Item extends SnakingItem = SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
> {
  planogram: { bays: SnakingBay<Item, Shelf>[] };
}

export interface LogicalShelf<
  Item extends SnakingItem = SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
> {
  shelves: Shelf[];
  bayId: string;
}

type TraversalRowKind = "shelf" | "pegboard";

export interface TraversalRow<Item extends SnakingItem = SnakingItem> {
  id: string;
  kind: TraversalRowKind;
  bayId: string;
  directionKey: string;
  /** Bottom-up physical position used to order rows top-to-bottom. */
  physicalPosition: number;
  items: Item[];
  logicalShelf?: LogicalShelf<Item>;
  pegboard?: SnakingPegboard<Item>;
  holeNoTop?: number;
}

export interface SnakingPattern {
  /** Direction for every logical shelf row and valid pegboard hole row. */
  rowDirections: Record<string, RowDirection>;
  /** Kept as the persisted/session key for the per-bay vertical order. */
  bayShelfOrders: Record<string, BayRowOrder>;
}

export interface SnakingInferenceResult {
  pattern: SnakingPattern;
  isConfident: boolean;
  hasPriorityOrderViolation: boolean;
  hasUnsupportedFixtures: boolean;
}

export const getPegboardRowId = (pegboardId: string, holeNoTop: number) =>
  `${pegboardId}:row:${holeNoTop}`;

export const getPatternRowDirections = (pattern: SnakingPattern) => pattern.rowDirections;

export const isValidPegboardHole = (hole: unknown): hole is PegboardHoleAddress =>
  typeof hole === "object" &&
  hole !== null &&
  "holeNoLeft" in hole &&
  "holeNoTop" in hole &&
  Number.isInteger(hole.holeNoLeft) &&
  Number(hole.holeNoLeft) >= 1 &&
  Number.isInteger(hole.holeNoTop) &&
  Number(hole.holeNoTop) >= 1;

const isValidPegboardAnchor = <Item extends SnakingItem>(
  placement: SnakingPegboardPlacement<Item>,
) => isValidPegboardHole(Array.isArray(placement.holes) ? placement.holes[0] : undefined);

export const hasInvalidPegboardPlacements = (pog: SnakingPog) =>
  pog.planogram.bays.some((bay) =>
    bay.pegboards.some((pegboard) =>
      pegboard.placements.some((placement) => !isValidPegboardAnchor(placement)),
    ),
  );

// Invalid pegboard anchors cannot be mapped to traversal rows, so inference must be blocked.
export const hasUnsupportedFixtures = (pog: SnakingPog) =>
  pog.planogram.bays.some((bay) => bay.shelves.some((shelf) => shelf.type !== "REGULAR")) ||
  hasInvalidPegboardPlacements(pog);

const isFinitePriority = (priority: number) => Number.isFinite(priority);

const getFirstShelf = <Item extends SnakingItem>(shelf: SnakingShelf<Item>) => {
  const visited = new Set<string>();
  let firstShelf = shelf;

  while (firstShelf.mergedLeft && !visited.has(firstShelf.uniqueId)) {
    visited.add(firstShelf.uniqueId);
    firstShelf = firstShelf.mergedLeft;
  }

  return firstShelf;
};

const getShelfChain = <Item extends SnakingItem>(shelf: SnakingShelf<Item>) => {
  const chain: SnakingShelf<Item>[] = [];
  const visited = new Set<string>();
  let current: SnakingShelf<Item> | null = getFirstShelf(shelf);

  while (current && !visited.has(current.uniqueId)) {
    chain.push(current);
    visited.add(current.uniqueId);
    current = current.mergedRight;
  }

  return chain;
};

export const getLogicalShelves = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
): LogicalShelf<Item, Shelf>[] => {
  const bayByShelfId = new Map<string, string>();
  const shelvesById = new Map<string, Shelf>();

  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      bayByShelfId.set(shelf.uniqueId, bay.uniqueId);
      shelvesById.set(shelf.uniqueId, shelf);
    }
  }

  const visited = new Set<string>();
  const logicalShelves: LogicalShelf<Item, Shelf>[] = [];

  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      const firstShelf = getFirstShelf(shelf);
      if (visited.has(firstShelf.uniqueId)) continue;

      const shelves = getShelfChain(firstShelf)
        .map((mergedShelf) => shelvesById.get(mergedShelf.uniqueId))
        .filter((mergedShelf): mergedShelf is Shelf => mergedShelf !== undefined);
      shelves.forEach((mergedShelf) => visited.add(mergedShelf.uniqueId));
      logicalShelves.push({
        shelves,
        bayId: bayByShelfId.get(firstShelf.uniqueId) || bay.uniqueId,
      });
    }
  }

  return logicalShelves;
};

const getRegularLogicalShelves = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
) =>
  getLogicalShelves(pog).filter((logicalShelf) =>
    logicalShelf.shelves.every((shelf) => shelf.type === "REGULAR"),
  );

const getItems = <Item extends SnakingItem>(logicalShelf: LogicalShelf<Item>) =>
  logicalShelf.shelves.flatMap((shelf) => shelf.items);

const getShelfPhysicalPosition = <Item extends SnakingItem>(
  shelf: SnakingShelf<Item>,
  fallback: number,
) => {
  const position = shelf.getYBottom?.() ?? shelf.yBottom;
  return position ?? -fallback;
};

export const getPegboardRowCount = (
  pegboard: Pick<SnakingPegboard, "height" | "holesOffsetVertical" | "holesGapVertical">,
) => getPegboardHoleCount(pegboard.height, pegboard.holesOffsetVertical, pegboard.holesGapVertical);

export const getPegboardRowPhysicalPosition = (
  pegboard: Pick<
    SnakingPegboard,
    "yBottom" | "height" | "holesOffsetVertical" | "holesGapVertical"
  >,
  holeNoTop: number,
) =>
  pegboard.yBottom +
  pegboard.height -
  pegboard.holesOffsetVertical -
  (holeNoTop - 1) * pegboard.holesGapVertical;

const comparePegboardPlacements = <Item extends SnakingItem>(
  first: SnakingPegboardPlacement<Item>,
  second: SnakingPegboardPlacement<Item>,
) => {
  const firstAnchor = first.holes[0];
  const secondAnchor = second.holes[0];
  return (
    (firstAnchor?.holeNoLeft ?? Number.MAX_SAFE_INTEGER) -
      (secondAnchor?.holeNoLeft ?? Number.MAX_SAFE_INTEGER) ||
    first.uniqueId.localeCompare(second.uniqueId)
  );
};

const isSnakingPegboard = (value: unknown): value is SnakingPegboard =>
  typeof value === "object" &&
  value !== null &&
  "placements" in value &&
  Array.isArray(value.placements);

const getPegboardRows = <Item extends SnakingItem>(
  bayId: string,
  pegboard: SnakingPegboard<Item>,
): TraversalRow<Item>[] => {
  const placementsByRow = new Map<number, SnakingPegboardPlacement<Item>[]>();

  for (const placement of pegboard.placements) {
    const anchor = Array.isArray(placement.holes) ? placement.holes[0] : undefined;
    if (!isValidPegboardAnchor(placement) || !anchor) continue;
    const placements = placementsByRow.get(anchor.holeNoTop) ?? [];
    placements.push(placement);
    placementsByRow.set(anchor.holeNoTop, placements);
  }

  const rowCount = getPegboardRowCount(pegboard);
  const rowNumbers = new Set<number>(Array.from({ length: rowCount }, (_, index) => index + 1));
  for (const rowNumber of placementsByRow.keys()) rowNumbers.add(rowNumber);

  return [...rowNumbers]
    .sort((first, second) => first - second)
    .map((holeNoTop) => {
      const placements = [...(placementsByRow.get(holeNoTop) ?? [])].sort(
        comparePegboardPlacements,
      );
      return {
        bayId,
        directionKey: getPegboardRowId(pegboard.uniqueId, holeNoTop),
        holeNoTop,
        id: getPegboardRowId(pegboard.uniqueId, holeNoTop),
        items: placements.map((placement) => placement.pegProduct),
        kind: "pegboard",
        pegboard,
        physicalPosition: getPegboardRowPhysicalPosition(pegboard, holeNoTop),
      };
    });
};

const compareTraversalRows = <Item extends SnakingItem>(
  first: TraversalRow<Item>,
  second: TraversalRow<Item>,
) => {
  // Positions are bottom-up, so descending order is visual top-to-bottom.
  const positionDifference = second.physicalPosition - first.physicalPosition;
  if (positionDifference !== 0) return positionDifference;

  // A regular shelf wins an exact physical tie, then stable row identity wins.
  if (first.kind !== second.kind) return first.kind === "shelf" ? -1 : 1;
  return first.id.localeCompare(second.id);
};

/**
 * Returns every logical REGULAR shelf and every valid pegboard hole row in
 * physical top-to-bottom order within its bay. Empty pegboard rows are
 * included so their direction can be configured and retained.
 */
export const getTraversalRows = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
): TraversalRow<Item>[] => {
  const logicalShelves = getRegularLogicalShelves(pog);
  const rowsByBay = new Map<string, TraversalRow<Item>[]>();
  const shelfIndexByBay = new Map<string, number>();

  for (const logicalShelf of logicalShelves) {
    const firstShelf = logicalShelf.shelves[0];
    if (!firstShelf) continue;
    const fallback = shelfIndexByBay.get(logicalShelf.bayId) ?? 0;
    shelfIndexByBay.set(logicalShelf.bayId, fallback + 1);
    const rows = rowsByBay.get(logicalShelf.bayId) ?? [];
    rows.push({
      bayId: logicalShelf.bayId,
      directionKey: firstShelf.uniqueId,
      id: firstShelf.uniqueId,
      items: getItems(logicalShelf),
      kind: "shelf",
      logicalShelf,
      physicalPosition: getShelfPhysicalPosition(firstShelf, fallback),
    });
    rowsByBay.set(logicalShelf.bayId, rows);
  }

  for (const bay of pog.planogram.bays) {
    const rows = rowsByBay.get(bay.uniqueId) ?? [];
    for (const pegboard of bay.pegboards) {
      if (isSnakingPegboard(pegboard)) {
        rows.push(...getPegboardRows(bay.uniqueId, pegboard));
      }
    }
    rows.sort(compareTraversalRows);
    rowsByBay.set(bay.uniqueId, rows);
  }

  return pog.planogram.bays.flatMap((bay) => rowsByBay.get(bay.uniqueId) ?? []);
};

const getPriorityComparisons = (priorities: number[]) => {
  let increasing = 0;
  let decreasing = 0;

  for (let index = 0; index < priorities.length - 1; index++) {
    if (priorities[index] < priorities[index + 1]) increasing++;
    if (priorities[index] > priorities[index + 1]) decreasing++;
  }

  return { increasing, decreasing };
};

const getRowDirection = <Item extends SnakingItem>(
  row: TraversalRow<Item>,
  pattern: SnakingPattern,
) => {
  const rowDirections = getPatternRowDirections(pattern);
  const directDirection = rowDirections[row.directionKey];
  if (directDirection) return directDirection;

  return undefined;
};

const getRowMinimum = <Item extends SnakingItem>(row: TraversalRow<Item>) => {
  const priorities = row.items.map((item) => item.cdt3Priority).filter(isFinitePriority);
  return priorities.length === 0 ? null : Math.min(...priorities);
};

const inferRowDirection = <Item extends SnakingItem>(
  row: TraversalRow<Item>,
  pattern: SnakingPattern,
) => {
  const priorities = row.items.map((item) => item.cdt3Priority);
  const { increasing, decreasing } = getPriorityComparisons(priorities);
  const hasInvalidPriority = priorities.some((priority) => !isFinitePriority(priority));
  const direction: RowDirection = decreasing > increasing ? "RTL" : "LTR";

  getPatternRowDirections(pattern)[row.directionKey] = direction;

  return {
    confident:
      priorities.length > 1 &&
      !hasInvalidPriority &&
      (increasing > decreasing || decreasing > increasing),
    violation: hasInvalidPriority || (increasing > 0 && decreasing > 0),
  };
};

const oppositeDirection = (direction: RowDirection): RowDirection =>
  direction === "LTR" ? "RTL" : "LTR";

const inferEmptyPegboardDirections = <Item extends SnakingItem>(
  rows: TraversalRow<Item>[],
  pattern: SnakingPattern,
) => {
  const rowsByPegboard = new Map<string, TraversalRow<Item>[]>();
  for (const row of rows) {
    if (row.kind !== "pegboard" || !row.pegboard) continue;
    const pegboardRows = rowsByPegboard.get(row.pegboard.uniqueId) ?? [];
    pegboardRows.push(row);
    rowsByPegboard.set(row.pegboard.uniqueId, pegboardRows);
  }

  for (const pegboardRows of rowsByPegboard.values()) {
    pegboardRows.sort((first, second) => (first.holeNoTop ?? 0) - (second.holeNoTop ?? 0));
    for (let index = 0; index < pegboardRows.length; index += 1) {
      const row = pegboardRows[index];
      if (!row || row.items.length > 0 || getRowDirection(row, pattern)) continue;

      const precedingIndex = [...pegboardRows.slice(0, index)]
        .reverse()
        .findIndex((candidate) => getRowDirection(candidate, pattern));
      const preceding = precedingIndex >= 0 ? pegboardRows[index - 1 - precedingIndex] : undefined;
      if (preceding) {
        const precedingDirection = getRowDirection(preceding, pattern);
        if (precedingDirection) {
          getPatternRowDirections(pattern)[row.directionKey] =
            oppositeDirection(precedingDirection);
          continue;
        }
      }

      const followingIndex = pegboardRows
        .slice(index + 1)
        .findIndex((candidate) => getRowDirection(candidate, pattern));
      const following = followingIndex >= 0 ? pegboardRows[index + 1 + followingIndex] : undefined;
      const followingDirection = following ? getRowDirection(following, pattern) : undefined;
      if (followingDirection) {
        const distance = followingIndex + 1;
        getPatternRowDirections(pattern)[row.directionKey] =
          distance % 2 === 0 ? followingDirection : oppositeDirection(followingDirection);
      } else {
        getPatternRowDirections(pattern)[row.directionKey] = "LTR";
      }
    }
  }
};

const inferBayRowOrder = <Item extends SnakingItem>(
  bayId: string,
  rows: TraversalRow<Item>[],
  pattern: SnakingPattern,
) => {
  const minima = rows
    .filter((row) => row.items.length > 0)
    .map(getRowMinimum)
    .filter((priority): priority is number => priority !== null);
  const { increasing, decreasing } = getPriorityComparisons(minima);
  const order: BayRowOrder = decreasing > increasing ? "BottomFirst" : "TopFirst";

  pattern.bayShelfOrders[bayId] = order;

  return {
    confident: minima.length > 1 && (increasing > decreasing || decreasing > increasing),
    violation: increasing > 0 && decreasing > 0,
  };
};

export const hasCompleteSnakingPattern = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
  pattern: SnakingPattern,
) => {
  const rowDirections = getPatternRowDirections(pattern);
  const hasAllBayOrders = pog.planogram.bays.every(
    (bay) => pattern.bayShelfOrders[bay.uniqueId] !== undefined,
  );
  if (!hasAllBayOrders) return false;

  return getTraversalRows(pog).every((row) => {
    const direction = rowDirections[row.directionKey];
    if (direction === undefined) return false;

    if (row.kind !== "shelf" || !row.logicalShelf) return true;
    return row.logicalShelf.shelves.every(
      (shelf) =>
        rowDirections[shelf.uniqueId] === undefined || rowDirections[shelf.uniqueId] === direction,
    );
  });
};

export const getTraversalFlattenedItems = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
  pattern: SnakingPattern,
): Item[] => {
  const rowsByBay = new Map<string, TraversalRow<Item>[]>();
  for (const row of getTraversalRows(pog)) {
    const rows = rowsByBay.get(row.bayId) ?? [];
    rows.push(row);
    rowsByBay.set(row.bayId, rows);
  }

  const items: Item[] = [];
  const rowDirections = getPatternRowDirections(pattern);

  for (const bay of pog.planogram.bays) {
    const rows = rowsByBay.get(bay.uniqueId) ?? [];
    const orderedRows =
      pattern.bayShelfOrders[bay.uniqueId] === "BottomFirst" ? [...rows].reverse() : rows;

    for (const row of orderedRows) {
      const direction = rowDirections[row.directionKey] ?? "LTR";
      items.push(...(direction === "RTL" ? [...row.items].reverse() : row.items));
    }
  }

  return items;
};

export const hasPriorityOrderViolation = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
  pattern: SnakingPattern,
) => {
  const items = getTraversalFlattenedItems(pog, pattern);

  if (items.some((item) => !isFinitePriority(item.cdt3Priority))) return true;

  return items.some(
    (item, index) => index > 0 && items[index - 1].cdt3Priority >= item.cdt3Priority,
  );
};

export const inferSnakingPattern = <
  Item extends SnakingItem,
  Shelf extends SnakingShelf<Item> = SnakingShelf<Item>,
>(
  pog: SnakingPog<Item, Shelf>,
): SnakingInferenceResult => {
  const pattern: SnakingPattern = { rowDirections: {}, bayShelfOrders: {} };
  const hasUnsupported = hasUnsupportedFixtures(pog);
  const rows = getTraversalRows(pog);
  let isConfident = !hasUnsupported;
  let priorityOrderViolation = false;

  for (const row of rows) {
    if (row.kind === "pegboard" && row.items.length === 0) continue;
    const result = inferRowDirection(row, pattern);
    isConfident = isConfident && result.confident;
    priorityOrderViolation = priorityOrderViolation || result.violation;
  }

  inferEmptyPegboardDirections(rows, pattern);

  for (const bay of pog.planogram.bays) {
    const result = inferBayRowOrder(
      bay.uniqueId,
      rows.filter((row) => row.bayId === bay.uniqueId),
      pattern,
    );
    isConfident = isConfident && result.confident;
    priorityOrderViolation = priorityOrderViolation || result.violation;
  }

  if (isConfident) {
    priorityOrderViolation = priorityOrderViolation || hasPriorityOrderViolation(pog, pattern);
  }

  return {
    pattern,
    isConfident,
    hasPriorityOrderViolation: priorityOrderViolation,
    hasUnsupportedFixtures: hasUnsupported,
  };
};
